import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronsUpDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { getJSON } from '@/lib/api'
import { cn } from '@/lib/utils'

type EventOption = { id: number; name: string; schedule: string }

export function RegistrationEventPicker({
  selectedLabel,
  onSelect,
}: {
  selectedLabel?: string
  onSelect: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const query = useQuery({
    queryKey: ['registration-event-options', search],
    queryFn: () =>
      getJSON<{ events: EventOption[] }>(
        `/api/v1/events?${new URLSearchParams({ registration_comparison: 'true', search, sort: 'date', direction: 'descending' })}`,
      ),
    enabled: open,
    retry: false,
  })
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Event"
          className="w-80 justify-between font-normal"
        >
          <span className={cn('truncate', !selectedLabel && 'text-muted-foreground')}>
            {selectedLabel ?? 'Choose an event'}
          </span>
          <ChevronsUpDown
            className="ml-2 size-4 shrink-0 text-muted-foreground"
            strokeWidth={1.5}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search events" value={search} onValueChange={setSearch} />
          <CommandList>
            <PickerStatus loading={query.isLoading} error={query.isError} />
            {(query.data?.events ?? []).map((event) => (
              <CommandItem
                key={event.id}
                value={String(event.id)}
                onSelect={() => {
                  onSelect(String(event.id))
                  setOpen(false)
                }}
              >
                <span className="truncate">{event.name}</span>
                <span className="ml-auto shrink-0 text-caption text-muted-foreground">
                  {event.schedule}
                </span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

function PickerStatus({ loading, error }: { loading: boolean; error: boolean }) {
  if (loading)
    return (
      <p role="status" className="p-3 text-body-sm text-muted-foreground">
        Loading events…
      </p>
    )
  if (error)
    return (
      <p role="alert" className="p-3 text-body-sm text-destructive">
        Could not load events.
      </p>
    )
  return <CommandEmpty>No events found</CommandEmpty>
}
